import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useSocket } from './SocketProvider';
import dealAPI from 'services/apis/deal';
import { NotificationCounterContext } from './NotificationCounterContext';

export function NotificationCounterProvider({ children }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [costCenters, setCostCenters] = useState(() => {
    // Sincronização inicial com localStorage
    try {
      const stored = localStorage.getItem('chat_negotiator_filters_cost_centers');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const { socket } = useSocket();
  const broadcastChannelRef = useRef(null);
  const costCentersRef = useRef(costCenters);

  // Manter ref atualizado
  useEffect(() => {
    costCentersRef.current = costCenters;
  }, [costCenters]);

  // Salva cost_centers no localStorage sempre que mudam
  useEffect(() => {
    try {
      localStorage.setItem(
        'chat_negotiator_filters_cost_centers',
        JSON.stringify(costCenters)
      );
    } catch (error) {
      console.error('Erro ao salvar filtro de centros de custo:', error);
    }
  }, [costCenters]);

  /**
   * Busca contagem de mensagens não lidas do servidor
   * Respeita o filtro de centro de custo
   * Atualiza o título da página com o contador
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      setLoading(true);

      const costCenterIds = costCentersRef.current?.length > 0
        ? costCentersRef.current.map(item => item.id).join(',')
        : null;

      const response = await dealAPI.get(`deals/countUnreadCount`, {
        params: {
          cost_centers: costCenterIds,
        },
      });

      let data = response.data;

      if (!data || data.length === 0) {
        setUnreadCount(0);
        document.title = 'Cooperplace';
        return;
      }

      // Filtrar apenas deals dos centros de custo selecionados (validação extra)
      const filteredData = costCentersRef.current?.length > 0
        ? data.filter(item => {
            const costCenterIds = costCentersRef.current.map(cc => cc.id);
            return costCenterIds.includes(item.cost_center_id);
          })
        : data;

      if (filteredData.length === 0) {
        setUnreadCount(0);
        document.title = 'Cooperplace';
        return;
      }

      // Calcular contagem de deals com notificações
      const count = filteredData.reduce((total, item) => {
        // Deal tem negociador + mensagens não lidas
        if (item?.negotiator_not_reads > 0 && item?.negotiator_id) {
          return total + 1;
        }
        // Deal sem negociador + mensagens não lidas
        if (!item?.messages_not_reads && !item?.negotiator_id) {
          return total + 1;
        }
        // Deal novo
        if (item?.new_deal) {
          return total + 1;
        }
        return total;
      }, 0);

      setUnreadCount(count);

      // Atualizar título da página com o contador
      if (count > 0) {
        document.title = count > 1
          ? `(${count}) Chats com novas mensagens!`
          : `(${count}) Chat com nova mensagem!`;
      } else {
        document.title = 'Cooperplace';
      }
    } catch (error) {
      console.error('Erro ao buscar contagem de não lidas:', error);
      document.title = 'Cooperplace';
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Buscar contagem quando costCenters mudam (local ou sincronizados de outra aba)
   * ou ao montar o componente
   */
  useEffect(() => {
    fetchUnreadCount();
  }, [costCenters, fetchUnreadCount]);

  /**
   * Sincroniza atualização entre abas sem polling
   */
  const broadcastRefresh = useCallback(() => {
    try {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({ type: 'refresh', ts: Date.now() });
      }
      localStorage.setItem('notification_counter_refresh', String(Date.now()));
    } catch (error) {
      // ignore
    }
  }, []);

  useEffect(() => {
    const handleChannelMessage = (event) => {
      if (event?.data?.type === 'refresh') {
        fetchUnreadCount();
      }
    };

    const handleStorage = (event) => {
      if (event.key === 'notification_counter_refresh') {
        fetchUnreadCount();
      }
      // Sincronizar costCenters quando filtro é mudado em outra aba
      if (event.key === 'chat_negotiator_filters_cost_centers') {
        try {
          const updated = event.newValue ? JSON.parse(event.newValue) : [];
          setCostCenters(updated);
        } catch (err) {
          console.error('Erro ao sincronizar filtro:', err);
        }
      }
    };

    if ('BroadcastChannel' in window) {
      broadcastChannelRef.current = new BroadcastChannel('notification_counter');
      broadcastChannelRef.current.addEventListener('message', handleChannelMessage);
    }

    window.addEventListener('storage', handleStorage);

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.removeEventListener('message', handleChannelMessage);
        broadcastChannelRef.current.close();
      }
      broadcastChannelRef.current = null;
      window.removeEventListener('storage', handleStorage);
    };
  }, [fetchUnreadCount]);

  /**
   * Escuta eventos do Socket.io para atualizar contador em tempo real
   */
  useEffect(() => {
    if (!socket) return;

    // Handler para nova negociação
    const handleNewDeal = () => {
      fetchUnreadCount();
      broadcastRefresh();
    };

    // Handler para mensagem recebida
    const handleMessage = () => {
      fetchUnreadCount();
      broadcastRefresh();
    };

    // Handler para chat encerrado
    const handleClosedChat = () => {
      fetchUnreadCount();
      broadcastRefresh();
    };

    // Handler para negociador atualizado
    const handleNegotiatorUpdate = () => {
      fetchUnreadCount();
      broadcastRefresh();
    };

    // Handler para lista recarregada
    const handleReloadList = () => {
      fetchUnreadCount();
      broadcastRefresh();
    };

    // Handler para mensagem lida
    const handleRead = () => {
      fetchUnreadCount();
      broadcastRefresh();
    };

    socket.on('newDeal', handleNewDeal);
    socket.on('message', handleMessage);
    socket.on('reloadClosedChat', handleClosedChat);
    socket.on('reloadChatUpdateNegotiator', handleNegotiatorUpdate);
    socket.on('reloadList', handleReloadList);
    socket.on('read', handleRead);

    return () => {
      socket.off('newDeal', handleNewDeal);
      socket.off('message', handleMessage);
      socket.off('reloadClosedChat', handleClosedChat);
      socket.off('reloadChatUpdateNegotiator', handleNegotiatorUpdate);
      socket.off('reloadList', handleReloadList);
      socket.off('read', handleRead);
    };
  }, [socket, fetchUnreadCount, broadcastRefresh]);

  const value = {
    unreadCount,
    loading,
    costCenters,
    setCostCenters,
    fetchUnreadCount,
  };

  return (
    <NotificationCounterContext.Provider value={value}>
      {children}
    </NotificationCounterContext.Provider>
  );
}

/**
 * Hook para usar o contador de notificações em qualquer componente
 * @returns {Object} { unreadCount, loading, costCenters, setCostCenters, fetchUnreadCount }
 */
export function useNotificationCounter() {
  const context = useContext(NotificationCounterContext);
  if (!context) {
    throw new Error('useNotificationCounter deve ser usado dentro de NotificationCounterProvider');
  }
  return context;
}
